import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { X, ShoppingCart, Trash2 } from 'lucide-react'
import { useCartStore } from '@/store/useCartStore'
import { useCartActions } from '@/hooks/useCart'
import { Button } from '@/components/common/Button'
import { CartItemRow } from './CartItemRow'
import type { CartItem } from '@/types/order'

interface SellerGroup {
  sellerId: string
  items: CartItem[]
  subtotal: number
}

export function CartDrawer() {
  const { items, drawerOpen, itemCount, error } = useCartStore()
  const closeDrawer = useCartStore((s) => s.closeDrawer)
  const { clear } = useCartActions()

  // Group items by seller
  const sellerGroups = useMemo<SellerGroup[]>(() => {
    const map = new Map<string, CartItem[]>()
    for (const item of items) {
      const existing = map.get(item.sellerId) || []
      existing.push(item)
      map.set(item.sellerId, existing)
    }
    return Array.from(map.entries()).map(([sellerId, groupItems]) => ({
      sellerId,
      items: groupItems,
      subtotal: groupItems.reduce((sum, i) => sum + i.price * i.quantity, 0),
    }))
  }, [items])

  if (!drawerOpen) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={closeDrawer} />

      {/* Drawer panel */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col animate-slide-up" role="dialog" aria-modal="true" aria-label="Shopping cart">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            <h2 className="text-lg font-semibold">Cart</h2>
            {itemCount > 0 && (
              <span className="bg-primary-100 text-primary-700 text-xs font-medium px-2 py-0.5 rounded-full">
                {itemCount}
              </span>
            )}
          </div>
          <button
            onClick={closeDrawer}
            className="p-2 hover:bg-slate-100 rounded-lg"
            aria-label="Close cart"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="p-3 mx-4 mt-2 bg-red-50 text-red-700 text-sm rounded-lg">
            {error}
          </div>
        )}

        {/* Cart contents */}
        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">Your cart is empty</p>
              <Button variant="primary" onClick={closeDrawer}>
                <Link to="/browse">Browse Listings</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {sellerGroups.map((group) => (
                <div key={group.sellerId} className="space-y-3">
                  {/* Seller header (only show if multiple sellers) */}
                  {sellerGroups.length > 1 && (
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-slate-500">
                        {group.items[0]?.title ? `From seller` : 'Seller'}
                      </p>
                    </div>
                  )}

                  {/* Items */}
                  {group.items.map((item) => (
                    <CartItemRow key={item.id} item={item} />
                  ))}

                  {/* Subtotal per seller */}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <span className="text-sm text-slate-500">Subtotal</span>
                    <span className="font-semibold">
                      ${group.subtotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-slate-200 p-4 space-y-3">
            {/* Total */}
            <div className="flex items-center justify-between">
              <span className="font-semibold">Total</span>
              <span className="text-xl font-bold">
                ${items
                  .reduce((sum, i) => sum + i.price * i.quantity, 0)
                  .toFixed(2)}
              </span>
            </div>

            <p className="text-xs text-slate-500">
              Shipping calculated at checkout
            </p>

            {/* Checkout button */}
            <Button
              variant="primary"
              fullWidth
              onClick={closeDrawer}
              className="!py-3"
            >
              <Link to="/checkout" className="flex items-center justify-center gap-2 w-full">
                Proceed to Checkout
              </Link>
            </Button>

            {/* Clear cart */}
            <button
              onClick={clear}
              className="flex items-center justify-center gap-1 text-sm text-slate-500 hover:text-red-600 w-full py-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear Cart
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
